import React from 'react';
import { Field, Form, Formik } from 'formik';
import { Box as RomboBox, FormField, Message } from '@getro/rombo';
import PropTypes from 'prop-types';
import { Text, Flex, Box } from 'rebass/styled-components';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import helperGet from '../../helpers/get';
import { useAddCompanyByNameMutation } from '../../services/organizationsV2';
import { trackEvent } from '../../helpers/analytics';
import NetworkSchema from '../../schemas/network';

const initialValues = {
  name: '',
};

const formSchema = Yup.object().shape({
  name: Yup.string().required(),
});

const AddCompanyWithoutDomain = ({ activeNetwork: network, goToCompaniesList, showAddCompanyWithDomain }) => {
  const history = useHistory();
  const [addCompanyByName, { isLoading }] = useAddCompanyByNameMutation();

  const handleSubmit = async ({ name }) => {
    try {
      const company = await addCompanyByName({ networkId: network.id, name }).unwrap();
      trackEvent('add_employer:submit_without_domain', {
        organization_id: company.id,
        network_id: network.id,
        name: company.name,
      });
      toast.success('Company successfully added.', { id: 'create_company_result' });
      history.push(`/networks/${network.slug}/companies/${company.slug}`);
    } catch (e) {
      toast.error(e?.data?.message || 'Something went wrong. Please try again.', { id: 'create_company_result' });
    }
  };

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={formSchema}>
        {({ status, isSubmitting }) => (
          <Form>
            <Box
              mt="80px"
              sx={{
                '.Box__title': {
                  fontWeight: 600,
                },
                '.Box__footer-back-button': {
                  fontWeight: 400,
                },
              }}
            >
              <RomboBox
                className="AddCompany"
                errorText={helperGet(status, 'error', null)}
                onCancelClick={goToCompaniesList}
                loading={isSubmitting || isLoading}
                editing
                saveAsSubmit
                title="Add Company"
                saveText="Add company"
                onBackClick={showAddCompanyWithDomain}
                backText="< Back"
              >
                <Text fontSize="14px">
                  Add a company without a website for <strong>early-stage</strong> and <strong>stealth</strong>{' '}
                  companies.
                </Text>
                <Message type="warning" my={[4]}>
                  <Flex flexDirection="column">
                    <Text fontWeight="medium" mb={[2]} fontSize={[1]}>
                      Some things you need to know about companies without a website:
                    </Text>
                    <Box as="ul" p={[0]} sx={{ listStylePosition: 'inside' }}>
                      <Text as="li">No company information will be added automatically</Text>
                      <Text as="li">All jobs must be posted</Text>
                      <Text as="li">Getro cannot pull extracted jobs if this company has them posted elsewhere</Text>
                    </Box>
                  </Flex>
                </Message>
                <Field type="text" name="name" component={FormField} label="Company name" />
              </RomboBox>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

AddCompanyWithoutDomain.propTypes = {
  activeNetwork: NetworkSchema.isRequired,
  goToCompaniesList: PropTypes.func.isRequired,
  showAddCompanyWithDomain: PropTypes.func.isRequired,
};

export default AddCompanyWithoutDomain;
